/**
 * component.js — the base class every interactive element extends.
 *
 * Provides three things, so individual components need almost no boot code:
 *   1. `this.refs`  — auto-collected [ref] descendants scoped to this component
 *   2. declarative event delegation — `on:click="method"` attributes in markup
 *   3. lifecycle hooks — mounted() / unmounted() / updatedCallback()
 *
 * Convention: custom-element tag names end in `-component`.
 * Import via the `@theme/component` import-map specifier.
 */

const DELEGATED_EVENTS = ['click', 'change', 'input', 'submit', 'keydown', 'pointerdown'];

/** Walk up from `el` to the nearest ancestor that is a Component instance. */
function nearestComponent(el) {
  let node = el;
  while (node) {
    if (node instanceof Component) return node;
    node = node.parentElement ?? node.getRootNode()?.host ?? null;
  }
  return null;
}

export class Component extends HTMLElement {
  /** requiredRefs = ['form', 'input'] → throws if any are missing on connect. */
  static requiredRefs = [];

  connectedCallback() {
    this._collectRefs();
    for (const name of this.constructor.requiredRefs) {
      if (!this.refs[name]) {
        console.warn(`[${this.localName}] missing required ref: "${name}"`);
      }
    }
    // Keep refs live if markup changes (deferred so connect stays cheap).
    this._observer = new MutationObserver(() => this._collectRefs());
    this._observer.observe(this, { childList: true, subtree: true, attributeFilter: ['ref'] });
    this.mounted?.();
  }

  disconnectedCallback() {
    this._observer?.disconnect();
    this.unmounted?.();
  }

  /** Fired after a Section-Rendering-API morph replaces our subtree. */
  updatedCallback() {}

  _collectRefs() {
    /** @type {Record<string, Element | Element[]>} */
    const refs = {};
    for (const el of this.querySelectorAll('[ref]')) {
      // Only claim refs that belong to THIS component, not a nested one.
      if (nearestComponent(el) !== this) continue;
      const raw = el.getAttribute('ref');
      if (!raw) continue;
      if (raw.endsWith('[]')) {
        const key = raw.slice(0, -2);
        (refs[key] ??= []).push(el);
      } else {
        refs[raw] = el;
      }
    }
    this.refs = refs;
  }
}

/* -------------------------------------------------------------------------
   Declarative event delegation.
   One capturing listener per event type on the document dispatches
   `on:<type>="[tag/]method?a=1"` attributes to the resolved component.
   ------------------------------------------------------------------------- */
function parseArgs(query) {
  const args = {};
  if (!query) return args;
  for (const pair of query.split('&')) {
    const [k, v = ''] = pair.split('=');
    args[decodeURIComponent(k)] = decodeURIComponent(v);
  }
  return args;
}

function handleDelegated(event) {
  const attr = `on:${event.type}`;
  const source = event.target.closest?.(`[${CSS.escape(attr)}]`);
  if (!source) return;

  const value = source.getAttribute(attr);
  if (!value) return;

  // Format: "method", "tag-name/method", optionally "...?a=1&b=2"
  const [path, query] = value.split('?');
  const [maybeTag, maybeMethod] = path.includes('/') ? path.split('/') : ['', path];
  const method = maybeMethod;

  const target = maybeTag
    ? source.closest(maybeTag)
    : nearestComponent(source);
  if (!target) return;

  // Harden against the custom-element upgrade race: force-upgrade before call.
  if (target.localName.includes('-')) customElements.upgrade(target);

  if (typeof target[method] !== 'function') {
    console.warn(`[${target.localName}] no method "${method}" for ${attr}`);
    return;
  }
  target[method](event, parseArgs(query));
}

let delegationReady = false;
export function initEventDelegation() {
  if (delegationReady) return;
  delegationReady = true;
  for (const type of DELEGATED_EVENTS) {
    document.addEventListener(type, handleDelegated, { capture: true });
  }
}

initEventDelegation();
